import { Injectable } from '@angular/core';
import { LoggingService } from './logging/logging.service';

/**
 * Cross-tab synchronization service using BroadcastChannel API.
 *
 * Solves the "Thinking..." bug where Tab2 shows indefinite loading:
 * - Tab1 creates BehaviorSubject for message content
 * - Tab2 loads from IndexedDB, creates SEPARATE BehaviorSubject
 * - Updates in Tab1's BehaviorSubject don't reach Tab2's BehaviorSubject
 *
 * Solution: Broadcast message content updates across all tabs
 * - Tab1 updates BehaviorSubject → broadcasts to all tabs
 * - Tab2 receives broadcast → updates its own BehaviorSubject
 * - All tabs stay synchronized in real-time
 */
@Injectable({ providedIn: 'root' })
export class CrossTabSyncService {
  private channel: BroadcastChannel | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();

  constructor(private log: LoggingService) {
    this.initChannel();
  }

  private initChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      this.log.warn('CrossTab', 'BroadcastChannel API not available in this browser');
      return;
    }

    try {
      this.channel = new BroadcastChannel('finderwise-messages');
      this.log.info('CrossTab', '✅ BroadcastChannel initialized (channel: finderwise-messages)');

      this.channel.onmessage = (event) => {
        const { type, data } = event.data;
        this.log.debug('CrossTab', `📨 Received broadcast: ${type}`, data);

        // Notify all listeners for this message type
        const typeListeners = this.listeners.get(type);
        if (typeListeners) {
          this.log.debug('CrossTab', `Found ${typeListeners.size} listener(s) for type: ${type}`);
          typeListeners.forEach(listener => {
            try {
              listener(data);
              this.log.debug('CrossTab', `✅ Listener called successfully for type: ${type}`);
            } catch (error) {
              this.log.error('CrossTab', `❌ Listener error for type: ${type}`, error);
            }
          });
        } else {
          this.log.warn('CrossTab', `⚠️ No listeners registered for type: ${type}`);
        }
      };

      this.channel.onmessageerror = (error) => {
        this.log.error('CrossTab', 'BroadcastChannel message error:', error);
      };

    } catch (error) {
      this.log.error('CrossTab', 'Failed to initialize BroadcastChannel:', error);
    }
  }

  /**
   * Broadcast a message to all other tabs.
   *
   * @param type Message type (e.g., 'message-content-update')
   * @param data Data to broadcast
   */
  broadcast(type: string, data: any): void {
    if (!this.channel) {
      this.log.warn('CrossTab', 'Cannot broadcast - BroadcastChannel not initialized');
      return;
    }

    try {
      this.log.debug('CrossTab', `📡 Broadcasting message: ${type}`, data);
      this.channel.postMessage({ type, data });
      this.log.debug('CrossTab', `✅ Broadcast sent successfully: ${type}`);
    } catch (error) {
      this.log.error('CrossTab', `❌ Broadcast failed for type: ${type}`, error);
    }
  }

  /**
   * Listen for messages of a specific type.
   *
   * @param type Message type to listen for
   * @param listener Callback function
   * @returns Unsubscribe function
   */
  listen(type: string, listener: (data: any) => void): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }

    this.listeners.get(type)!.add(listener);
    this.log.info('CrossTab', `👂 Listener registered for type: ${type} (total: ${this.listeners.get(type)!.size})`);

    // Return unsubscribe function
    return () => {
      const typeListeners = this.listeners.get(type);
      if (typeListeners) {
        typeListeners.delete(listener);
        this.log.info('CrossTab', `🔕 Listener unregistered for type: ${type} (remaining: ${typeListeners.size})`);

        // Clean up empty sets
        if (typeListeners.size === 0) {
          this.listeners.delete(type);
          this.log.debug('CrossTab', `🧹 Cleaned up empty listener set for type: ${type}`);
        }
      }
    };
  }

  /**
   * Close the broadcast channel (cleanup).
   */
  destroy(): void {
    if (this.channel) {
      this.log.info('CrossTab', 'Closing BroadcastChannel');
      this.channel.close();
      this.channel = null;
    }

    this.log.info('CrossTab', `Clearing ${this.listeners.size} listener type(s)`);
    this.listeners.clear();
  }
}
